/**
 * @module AuroraUtils
 * @description Singleton module that manages Aurora Utilities.
 */
const AuroraUtils = (function () {
  /**
   * An object representing the module's settings.
   *
   * @private
   * @type {object} settings
   * @description The initial settings for the singleton object.
   */
  const settings = {
    name: "AuroraUtils",
    debugging: false,
  };

  const classes = {
    scroll: {
      atTop: "scroll-position--at-top",
      scrolled: "scroll-position--scrolled",
      scrollingDown: "scroll-position--scrolling-down",
      scrollingUp: "scroll-position--scrolling-up",
    },
  };

  const elements = {
    html: document.documentElement,
    noJsHidden: document.querySelectorAll(".no-js-hidden") || [],
    core: document.querySelectorAll("body, footer, header, main") || [],
    overlay: {
      global: document.querySelector(".js--loading-overlay-global") || false,
    },
  };

  const moneyFormat = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  });

  const scrollPosition = {
    initial: window.pageYOffset || document.documentElement.scrollTop,
    current: window.pageYOffset || document.documentElement.scrollTop,
    initial: window.pageYOffset || document.documentElement.scrollTop,
    previous: window.pageYOffset || document.documentElement.scrollTop,
    down: false,
  };

  const cssVarState = {
    rafId: null,
    pendingForce: false,
    values: {},
  };

  const requestFrame = window.requestAnimationFrame ? window.requestAnimationFrame.bind(window) : (callback) => window.setTimeout(callback, 16);

  const cancelFrame = window.cancelAnimationFrame ? window.cancelAnimationFrame.bind(window) : window.clearTimeout.bind(window);

  const formatMoney = (amount = 0) => {
    return moneyFormat.format(amount);
  };

  function getCookie(name) {
    let matches = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, "\\$1") + "=([^;]*)"));
    return matches ? decodeURIComponent(matches[1]) : undefined;
  }

  const getPositiveNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) && num > 0 ? num : 0;
  };

  const normalizeText = (value) => {
    if (value === undefined || value === null) {
      return "";
    }

    return String(value).trim();
  };

  function getResolvedOrderLimitContextFromLineItem(lineItem = {}, lineItemAdditionalData = {}, options = {}) {
    const { fallbackToInventory = true, isSiteFrench = false } = options;
    const itemComponents = Array.isArray(lineItemAdditionalData?.item_components) ? lineItemAdditionalData.item_components : [];
    const isBundle = itemComponents.length > 0;

    const getComponentPropertyValue = (component = {}, propertyName = "") => {
      const properties = component?.properties;

      if (Array.isArray(properties)) {
        const property = properties.find((entry) => entry?.name === propertyName);
        return property?.value;
      }

      return properties?.[propertyName];
    };

    const getComponentOrderLimit = (component = {}) => {
      return getPositiveNumber(getComponentPropertyValue(component, "_order_limit"));
    };

    // TODO (AW-3840): Verify nothing in aurora-app.js or Springbok depends on _order_limit_message / _fr_order_limit_message before removing. Message is now generated from window.theme.locales in aurora-templates.js.
    const getComponentOrderLimitMessage = (component = {}) => {
      const messageProperty = isSiteFrench ? "_fr_order_limit_message" : "_order_limit_message";
      return normalizeText(getComponentPropertyValue(component, messageProperty));
    };

    const getComponentVariantInventory = (component = {}) => {
      const inventoryFromProperties = getPositiveNumber(getComponentPropertyValue(component, "_variant_inventory"));
      if (inventoryFromProperties > 0) {
        return inventoryFromProperties;
      }

      return getPositiveNumber(
        component?.variant_inventory ?? component?.inventory_quantity ?? component?.inventory ?? component?.available_quantity
      );
    };

    const getLimitingCandidate = (candidates = []) => {
      const validCandidates = candidates
        .map((candidate = {}) => ({
          ...candidate,
          limit: getPositiveNumber(candidate.limit),
          message: normalizeText(candidate.message),
        }))
        .filter((candidate) => candidate.limit > 0);

      if (validCandidates.length === 0) {
        return null;
      }

      const minimumLimit = Math.min(...validCandidates.map((candidate) => candidate.limit));
      const limitingCandidates = validCandidates.filter((candidate) => candidate.limit === minimumLimit);
      const limitingComponentWithMessage = limitingCandidates.find((candidate) => candidate.sourceType?.startsWith("component") && candidate.message);

      if (limitingComponentWithMessage) {
        return limitingComponentWithMessage;
      }

      return limitingCandidates[0];
    };

    // TODO (AW-3840): Verify nothing in aurora-app.js or Springbok depends on _order_limit_message / _fr_order_limit_message before removing. Message is now generated from window.theme.locales in aurora-templates.js.
    const lineItemMessage = normalizeText(
      isSiteFrench
        ? (lineItem?.properties?._fr_order_limit_message || lineItem?.properties?._order_limit_message)
        : lineItem?.properties?._order_limit_message
    );
    const bundleMessage = normalizeText(isSiteFrench ? lineItemAdditionalData?.order_limit_message_fr : lineItemAdditionalData?.order_limit_message);

    if (isBundle) {
      const explicitBundleCandidates = [];
      const bundleOrderLimit = getPositiveNumber(lineItemAdditionalData?.order_limit);

      if (bundleOrderLimit > 0) {
        explicitBundleCandidates.push({
          limit: bundleOrderLimit,
          message: bundleMessage,
          sourceType: "bundle_explicit",
        });
      }

      itemComponents.forEach((component, index) => {
        const componentOrderLimit = getComponentOrderLimit(component);

        if (componentOrderLimit > 0) {
          explicitBundleCandidates.push({
            limit: componentOrderLimit,
            message: getComponentOrderLimitMessage(component),
            sourceType: "component_explicit",
            sourceIndex: index,
          });
        }
      });

      const explicitLimitingCandidate = getLimitingCandidate(explicitBundleCandidates);
      if (explicitLimitingCandidate) {
        return {
          orderLimit: explicitLimitingCandidate.limit,
          orderLimitMessage: explicitLimitingCandidate.message,
        };
      }

      if (!fallbackToInventory) {
        return {
          orderLimit: 0,
          orderLimitMessage: "",
        };
      }

      const componentInventoryCandidates = [];
      itemComponents.forEach((component, index) => {
        const componentInventory = getComponentVariantInventory(component);

        if (componentInventory > 0) {
          componentInventoryCandidates.push({
            limit: componentInventory,
            message: getComponentOrderLimitMessage(component),
            sourceType: "component_inventory",
            sourceIndex: index,
          });
        }
      });

      const inventoryLimitingCandidate = getLimitingCandidate(componentInventoryCandidates);
      return {
        orderLimit: inventoryLimitingCandidate?.limit ?? 0,
        orderLimitMessage: inventoryLimitingCandidate?.message ?? "",
      };
    }

    const sourceOrderLimit = getPositiveNumber(lineItem?.properties?._order_limit);
    if (sourceOrderLimit > 0) {
      return {
        orderLimit: sourceOrderLimit,
        orderLimitMessage: lineItemMessage,
      };
    }

    if (!fallbackToInventory) {
      return {
        orderLimit: 0,
        orderLimitMessage: "",
      };
    }

    return {
      orderLimit: getPositiveNumber(lineItem?.properties?._variant_inventory),
      orderLimitMessage: lineItemMessage,
    };
  }

  function getResolvedOrderLimitFromLineItem(lineItem = {}, lineItemAdditionalData = {}, options = {}) {
    const { fallbackToInventory = true, isSiteFrench = false } = options;
    const { orderLimit } = getResolvedOrderLimitContextFromLineItem(lineItem, lineItemAdditionalData, {
      fallbackToInventory,
      isSiteFrench,
    });
    return orderLimit;
  }


  function getResolvedProductTitleFromLineItem(lineItem = {}, lineItemAdditionalData = {}, options = {}) {
    const { isSiteFrench = false } = options;
    const fallbackTitle = normalizeText(lineItem?.product_title) || normalizeText(lineItem?.title);

    if (!isSiteFrench) {
      return fallbackTitle;
    }

    const itemComponents = Array.isArray(lineItemAdditionalData?.item_components) ? lineItemAdditionalData.item_components : [];
    const isBundle = itemComponents.length > 0;
    const lineItemTitleFr = normalizeText(lineItem?.properties?._fr_product_title);

    // For bundles, rely on Shopify/locale-translated cart title as source of truth.
    if (isBundle) {
      return fallbackTitle || lineItemTitleFr;
    }

    return lineItemTitleFr || fallbackTitle;
  }

  function getResolvedProductTitleFromBundleComponent(component = {}, options = {}) {
    const { isSiteFrench = false } = options;
    const fallbackTitle = normalizeText(component?.product_title) || normalizeText(component?.title);

    if (!isSiteFrench) {
      return fallbackTitle;
    }

    const titleFr = normalizeText(component?.properties?._fr_product_title) || normalizeText(component?.product_title_fr);
    return titleFr || fallbackTitle;
  }

  const getRandomNumber = (min, max) => {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  const getHeaderMainMenuOffsetBottom = () => {
    const header = document.querySelector(".header-main-menu");
    if (!header) return 0;

    const headerOffset = header.offsetTop;
    const headerHeight = header.offsetHeight;
    return headerOffset + headerHeight - 1;
  };

  const isDateExpired = (date = 0) => {
    return date < Date.now() ? true : false;
  };

  const isSiteFrench = () => {
    return window.location.href.includes(".com/fr");
  };

  const debounce = (func, delay) => {
    let inDebounce;
    return function () {
      const context = this;
      const args = arguments;
      clearTimeout(inDebounce);
      inDebounce = setTimeout(() => func.apply(context, args), delay);
    };
  };

  const logFormData = (formData = {}) => {
    for (const field of formData.entries()) {
      console.log({
        fieldName: field[0],
        fieldValue: field[1],
      });
    }
  };

  const removeNoJsClass = () => {
    elements.noJsHidden.forEach((el) => {
      el.classList.remove("no-js-hidden");
    });
  };

  /**
   * Deletes a cookie by setting it to empty with an expiry in the past.
   *
   * Path and domain (if provided) must match how the cookie was originally set, or the browser
   * will not remove it. Omit domain for cookies set on the current host; pass path only when
   * the cookie was set with a specific path.
   *
   * @param {string} name - The cookie name to delete.
   * @param {Object} [options] - Optional attributes that must match how the cookie was set.
   * @param {string} [options.path="/"] - Path; must match the cookie's path when set (default "/").
   * @param {string} [options.domain=""] - Domain; only required if the cookie was set with a domain.
   * @returns {void}
   *
   * @author Brenden Mersey
   */
  function deleteCookie(name = "", options = {}) {
    if (!name) {
      console.error("[ AuroraUtils ] :: deleteCookie() :: cookie name is required", { name, options });
      return;
    }

    const { path = "/", domain = "" } = options;
    let cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
    if (domain) {
      cookie += `; domain=${domain}`;
    }
    document.cookie = cookie;
  }

  function setCookie(name, value, attributes = {}) {
    attributes = {
      path: "/",
      // add other defaults here if necessary
      ...attributes,
    };

    if (attributes.expires instanceof Date) {
      attributes.expires = attributes.expires.toUTCString();
    }

    let updatedCookie = encodeURIComponent(name) + "=" + encodeURIComponent(value);

    for (let attributeKey in attributes) {
      updatedCookie += "; " + attributeKey;
      let attributeValue = attributes[attributeKey];
      if (attributeValue !== true) {
        updatedCookie += "=" + attributeValue;
      }
    }

    document.cookie = updatedCookie;
  }

  const swapNullForValue = (obj = {}, value = "") => {
    const swappedObj = {};

    for (const prop in obj) {
      if (null === obj[prop]) {
        swappedObj[prop] = value;
      } else {
        swappedObj[prop] = obj[prop];
      }
    }

    return swappedObj;
  };

  const getCurrentScrollPosition = () => {
    return window.pageYOffset || document.documentElement.scrollTop;
  };

  const setCSSVar = (name = "", value = "", forceUpdate = false) => {
    if (!name) return;

    if (!forceUpdate && cssVarState.values[name] === value) return;

    cssVarState.values[name] = value;
    elements.html.style.setProperty(name, value);
  };

  const getCSSVarValues = () => {
    const values = {
      "--theme-viewport-height": `${window.innerHeight}px`,
      "--theme-header-main-menu-offset-bottom": `${getHeaderMainMenuOffsetBottom()}px`,
    };

    elements.core.forEach((element) => {
      const elementHeight = element.offsetHeight - 1;
      values[`--theme-${element.tagName.toLowerCase()}-height`] = `${elementHeight}px`;
    });

    return values;
  };

  const applyCSSVars = (forceUpdate = false) => {
    const values = getCSSVarValues();
    Object.entries(values).forEach(([name, value]) => setCSSVar(name, value, forceUpdate));
  };

  const setCSSVars = (options = {}) => {
    const { force = false, immediate = false } = options;

    if (immediate) {
      if (cssVarState.rafId !== null) {
        cancelFrame(cssVarState.rafId);
        cssVarState.rafId = null;
      }

      const forceUpdate = force || cssVarState.pendingForce;
      cssVarState.pendingForce = false;
      applyCSSVars(forceUpdate);
      return;
    }

    cssVarState.pendingForce = cssVarState.pendingForce || force;

    if (cssVarState.rafId !== null) return;

    cssVarState.rafId = requestFrame(() => {
      cssVarState.rafId = null;
      const forceUpdate = cssVarState.pendingForce;
      cssVarState.pendingForce = false;
      applyCSSVars(forceUpdate);
    });
  };

  async function fetchCartAdditionalData() {
    try {
      const shopifyRoot = window.Shopify?.routes?.root || "/";
      const response = await fetch(`${shopifyRoot}cart?view=bundle-components`);
      if (!response.ok) {
        throw new Error(`fetchCartAdditionalData failed: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching cart additional data", error);
      return null;
    }
  }

  async function getCartAdditionalData() {
    const parsedData = await fetchCartAdditionalData();
    if (!parsedData) {
      return {};
    }

    try {
      const items = Array.isArray(parsedData) ? parsedData : parsedData?.items;
      if (!Array.isArray(items)) {
        return {};
      }

      const normalizeOrderLimit = (orderLimitRaw) => {
        if (orderLimitRaw === undefined || orderLimitRaw === null || orderLimitRaw === "") {
          return null;
        }

        const orderLimit = Number(orderLimitRaw);
        return Number.isFinite(orderLimit) ? orderLimit : null;
      };

      return items.reduce((acc, item) => {
        if (item && typeof item === "object" && item.id != null) {
          acc[item.id] = {
            item_components: Array.isArray(item.item_components) ? item.item_components : [],
            order_limit: normalizeOrderLimit(item.order_limit),
            order_limit_message: normalizeText(item.order_limit_message),
            order_limit_message_fr: normalizeText(item.order_limit_message_fr),
          };
        }
        return acc;
      }, {});
    } catch (error) {
      console.error("Error parsing cart additional data:", error);
      return {};
    }
  }

  /**
   * Checks if the given attributes array contains the bundle attribute key.
   * @param {Array<{key: string}>} [attributes=[]]
   * @returns {boolean}
   */
  function getIsBundle(properties = {}) {
    const BUNDLE_ATTRIBUTE_KEY = "__cartBundleId";
    return Boolean(properties && typeof properties === "object" && properties[BUNDLE_ATTRIBUTE_KEY]);
  }

  const setScrollPositionWithClasses = () => {
    scrollPosition.previous = scrollPosition.current;
    scrollPosition.current = getCurrentScrollPosition();

    const scrolled = scrollPosition.current > 5;
    const atTop = scrollPosition.current == 0;
    const down = scrollPosition.current > scrollPosition.previous ? true : false;

    document.querySelector(".header__container .announcements")?.classList.toggle("announcements--hidden", scrolled);

    elements.core.forEach((element) => {
      element.classList[scrolled ? "add" : "remove"](classes.scroll.scrolled);
      element.classList[atTop ? "add" : "remove"](classes.scroll.atTop);
      element.classList[down ? "add" : "remove"](classes.scroll.scrollingDown);
      element.classList[down ? "remove" : "add"](classes.scroll.scrollingUp);
    });
  };

  const throttle = (func, limit) => {
    let lastFunc;
    let lastRan;
    return function () {
      const context = this;
      const args = arguments;
      if (!lastRan) {
        func.apply(context, args);
        lastRan = Date.now();
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(
          function () {
            if (Date.now() - lastRan >= limit - 2) {
              func.apply(context, args);
              lastRan = Date.now();
            }
          },
          limit - (Date.now() - lastRan)
        );
      }
    };
  };

  /**
   * Returns unique array.
   *
   * @function uniqueArray
   * @param {string[]} arr - An array of strings.
   * @param {string} str - The string to be added to array.
   * @returns {string[]} An array of unique strings.
   * @author Brenden Mersey
   */
  function uniqueArray(arr = [], str = "") {
    arr.push(str);

    return [...new Set(arr)];
  }

  const init = () => {
    removeNoJsClass();
    setCSSVars({
      force: true,
      immediate: true,
    });
    setScrollPositionWithClasses();
  };

  return {
    debounce,
    deleteCookie,
    formatMoney,
    getCookie,
    getCurrentScrollPosition,
    getRandomNumber,
    init,
    isDateExpired,
    isSiteFrench,
    logFormData,
    removeNoJsClass,
    setCSSVars,
    setCookie,
    setScrollPositionWithClasses,
    swapNullForValue,
    throttle,
    uniqueArray,
    settings,
    getCartAdditionalData,
    getIsBundle,
    getResolvedOrderLimitFromLineItem,
    getResolvedProductTitleFromLineItem,
    getResolvedProductTitleFromBundleComponent,
  };
})();
